import { countryLocalNames } from "@/components/Inputs/CountrySelect/countryLocalNames.ts";
import { Country } from "@/helpers/types/types.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { RootAttribute } from "@/stores/EngineStore/enums/RootAttribute.ts";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const DEFAULT_PRIORITY_COUNTRIES = ["HRV", "SVN", "DEU", "AUT", "ITA", "FRA"];

export const matchesLocalName = (iso3Code: string, query: string, lang: string): boolean => {
  const localNames = countryLocalNames[iso3Code];
  if (!localNames) return false;
  if (localNames[lang]?.toLowerCase().includes(query)) return true;
  // For English, also allow searching by Croatian local names
  return !!(lang === "en" && localNames.hr?.toLowerCase().includes(query));
};

export const useCountrySearch = (countries: Country[], search: string) => {
  const { i18n } = useTranslation();
  const rootAttributes = useEngineStore((state) => state.rootAttributes);
  const currentLang = i18n.language;

  const priorityCodes = useMemo(() => {
    const customPriority = rootAttributes[RootAttribute.PriorityCountries];
    return customPriority?.length ? customPriority : DEFAULT_PRIORITY_COUNTRIES;
  }, [rootAttributes]);

  const priorityCountries = useMemo(() => {
    return priorityCodes
      .map((code) => countries.find((c) => c.iso3Code === code))
      .filter((c): c is NonNullable<typeof c> => !!c);
  }, [countries, priorityCodes]);

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || matchesLocalName(c.iso3Code, q, currentLang),
    );
  }, [countries, search, currentLang]);

  return { priorityCodes, priorityCountries, filteredCountries };
};
